import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import PlatformCard from '@/components/base/PlatformCard';
import ArticleCard from '@/components/base/ArticleCard';
import FAQSection from '@/components/base/FAQSection';
import { platforms, featuredPlatformIds } from '@/mocks/platforms';
import { categories } from '@/mocks/categories';
import { articles, featuredArticleSlugs } from '@/mocks/articles';
import { faqItems } from '@/mocks/faq';
import { useState } from 'react';

const featuredPlatforms = platforms.filter((p) => featuredPlatformIds.includes(p.id));
const featuredArticles = articles.filter((a) => featuredArticleSlugs.includes(a.slug));
const latestArticles = articles.slice(0, 6);

export default function Home() {
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribeStatus, setSubscribeStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [subscribeMessage, setSubscribeMessage] = useState('');

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setSubscribeStatus('success');
      setSubscribeMessage('订阅成功！感谢您的关注。');
      setSubscribeEmail('');
      return;
    }
    try {
      const formData = new FormData(form);
      const res = await fetch('https://readdy.ai/api/form/d9son9d8o1vn02ekuj6g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (res.ok && parsed?.code === 'OK') {
        setSubscribeStatus('success');
        setSubscribeMessage('订阅成功！感谢您的关注，我们会为您推送最新的免费看片资讯。');
        setSubscribeEmail('');
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || '订阅失败，请稍后再试';
        if (serverMsg.includes('spam')) {
          setSubscribeStatus('error');
          setSubscribeMessage('订阅失败，请稍后再试');
        } else {
          setSubscribeStatus('error');
          setSubscribeMessage(serverMsg);
        }
      }
    } catch {
      setSubscribeStatus('error');
      setSubscribeMessage('网络错误，请检查您的网络连接后重试');
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '免费看片推荐',
        url: 'https://mianfeikanpian.com',
        description: '精选正版免费看片平台，涵盖电影、电视剧、纪录片等海量内容。',
        sameAs: [],
      },
      {
        '@type': 'WebSite',
        name: '免费看片推荐',
        url: 'https://mianfeikanpian.com',
        description: '精选正版免费看片平台推荐，免费看片合集2024，高清免费看片资源一站直达。',
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'WebPage',
        name: '免费看片平台推荐·正版高清资源一站直达',
        description: '想找免费看片的好去处？本页精选多个合法正版免费看片平台，涵盖电影、电视剧、纪录片等海量内容。',
        url: 'https://mianfeikanpian.com/',
        isPartOf: { '@id': 'https://mianfeikanpian.com/#website' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ],
  };

  return (
    <>
      <SEOHead
        title="免费看片平台推荐·正版高清资源一站直达 | 免费看片合集2024"
        description="想找免费看片的好去处？本页精选多个合法正版免费看片平台，涵盖电影、电视剧、纪录片等海量内容，无需付费即可畅享高清画质，帮你快速找到最适合的免费看片渠道，告别广告骚扰与版权风险。"
        keywords="免费看片,免费看片平台推荐,正版免费看片网站,手机免费看片,免费看片不卡顿,高清免费看片资源,免费看片合集2024"
        canonicalUrl="https://mianfeikanpian.com/"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />

      <div className="min-h-screen">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=cinematic%20warm%20home%20theater%20setup%20with%20large%20screen%20displaying%20movie%20thumbnails%2C%20comfortable%20living%20room%20atmosphere%2C%20warm%20ambient%20lighting%2C%20cozy%20and%20modern%20interior%2C%20soft%20golden%20and%20coral%20toned%20color%20palette%2C%20artistic%20editorial%20photography%20style%2C%20rich%20textures%20and%20depth&width=1800&height=1000&seq=hero-bg-home&orientation=landscape"
            alt="免费看片平台推荐 - 精选正版高清影视资源"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4 w-full max-w-3xl">
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-4 leading-tight">
                免费看片平台推荐
              </h1>
              <p className="text-base md:text-lg text-background-50/85 mb-2 leading-relaxed">
                正版高清资源一站直达
              </p>
              <p className="text-sm md:text-base text-background-50/70 mb-8 max-w-xl mx-auto leading-relaxed">
                精选多个合法正版免费看片平台，涵盖电影、电视剧、纪录片等海量内容，无需付费即可畅享高清画质，告别广告骚扰与版权风险。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/zonghe"
                  className="px-8 py-3 rounded-lg bg-primary-500 text-background-50 font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  探索平台
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 rounded-lg border border-background-50/30 text-background-50 font-medium hover:bg-background-50/10 transition-colors whitespace-nowrap cursor-pointer"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Banner */}
        <section className="w-full bg-background-100 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                { num: '12+', label: '精选平台' },
                { num: '1000+', label: '影视资源' },
                { num: '100%', label: '正版授权' },
                { num: '6', label: '内容分类' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="font-heading text-xl md:text-2xl font-bold text-primary-500">{stat.num}</p>
                  <p className="text-xs md:text-sm text-foreground-600 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Platforms */}
        <section className="w-full py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                精选免费看片平台
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                从海量平台中为您甄选最优质的免费看片渠道，所有平台均经过实测，确保正版授权和高清观影体验。
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPlatforms.map((platform) => (
                <PlatformCard key={platform.id} {...platform} />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/zonghe"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-background-300 text-foreground-700 font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看全部平台
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                按分类浏览平台
              </h2>
              <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto">
                根据您的观影偏好，选择对应的内容分类，快速找到最适合您的免费看片平台。
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300 group cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                    <i className={`${cat.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-1">{cat.name}</h3>
                  <p className="text-xs text-foreground-500 line-clamp-2">{cat.description}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="w-full py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-10">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                  最新评测文章
                </h2>
                <p className="text-sm md:text-base text-foreground-600">
                  深入了解每个平台的特色与使用技巧
                </p>
              </div>
              <Link
                to="/category/zonghe"
                className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 whitespace-nowrap cursor-pointer"
              >
                更多文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="flex flex-col gap-4">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} {...article} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full py-14 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
              <p className="text-sm md:text-base text-foreground-600">
                关于免费看片平台的常见疑问，我们一一为您解答
              </p>
            </div>
            <FAQSection items={faqItems} />
          </div>
        </section>

        {/* Newsletter */}
        <section className="w-full py-14 md:py-20">
          <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
            <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-full bg-accent-100">
              <i className="ri-mail-send-line text-2xl text-accent-600"></i>
            </div>
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-3">
              获取最新免费看片资讯
            </h2>
            <p className="text-sm md:text-base text-foreground-600 mb-6">
              订阅我们的邮件，第一时间获取新平台推荐和限免活动信息。
            </p>
            <form
              onSubmit={handleSubscribe}
              data-readdy-form
              className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
            >
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
              <input
                type="email"
                name="email"
                value={subscribeEmail}
                onChange={(e) => setSubscribeEmail(e.target.value)}
                placeholder="请输入您的邮箱地址"
                required
                className="flex-1 px-4 py-3 rounded-lg border border-background-300 text-sm bg-background-50 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
              />
              <button
                type="submit"
                className="px-6 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即订阅
              </button>
            </form>
            {subscribeStatus !== 'idle' && (
              <p className={`mt-3 text-sm ${
                subscribeStatus === 'success' ? 'text-primary-500' : 'text-red-500'
              }`}>
                {subscribeMessage}
              </p>
            )}
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}